/* eslint-disable no-restricted-syntax */

/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useState } from 'react';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';

import { Grid, RadioCard, RadioGroup, Typography2 } from '@coursera/cds-core';

// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { PrivateSessionDrawerContext } from 'bundles/enterprise-admin-drawers/components/PrivateSessionEditDrawerContent';
import { DrawerInfo } from 'bundles/enterprise-admin-drawers/components/components/DrawerInfoBoxes';
import { useProgramAdminTypeAndPermissionsContext } from 'bundles/enterprise-admin-program-catalog/components/ProgramAdminTypeAndPermissionsContextProvider';
import enterpriseAdminUser from 'bundles/enterprise-admin/enterprise-admin-index';
import { TrackedButton } from 'bundles/enterprise-ui/components';

import _t from 'i18n!nls/admin-dashboard';

export type Props = {
  isOpen: boolean;
  isS12n?: boolean;
  isProfessionalCertificate?: boolean;
  isAutoEnrolling: boolean;
  hasUpdatedAutoEnroll: boolean;
  isAvailabilityEnded: boolean;
  fixedEndDate?: string;
  onChangeAutoEnroll: (e: React.ChangeEvent<HTMLInputElement>) => void;
  productId?: string;
};

const warningStyle = css`
  margin: 16px 0 0 -40px;
`;

const getAutoEnrollHeader = (isS12n?: boolean, isProfessionalCertificate?: boolean) => {
  if (isS12n) {
    return isProfessionalCertificate
      ? _t('Auto-enroll learners in this Professional Certificate?')
      : _t('Auto-enroll learners in this Specialization?');
  } else {
    return _t('Auto-enroll learners in this course?');
  }
};

export const AutoEnrollStep: React.FC<Props> = ({
  isAutoEnrolling,
  fixedEndDate,
  hasUpdatedAutoEnroll,
  isS12n,
  isProfessionalCertificate,
  onChangeAutoEnroll,
}) => {
  const getNoOptionSupportText = () => {
    if (isS12n) {
      return isProfessionalCertificate
        ? _t(
            'Future learners who join this learning program will not be automatically enrolled in this Professional Certificate. Learners already enrolled will not be automatically unenrolled.'
          )
        : _t(
            'Future learners who join this learning program will not be automatically enrolled in this Specialization. Learners already enrolled will not be automatically unenrolled.'
          );
    } else {
      return _t(
        'Automatic enrollment cannot be enabled for this course because the availability period selected has ended. Please select a different availability option or setup a custom availability.'
      );
    }
  };

  return (
    <RadioGroup
      data-testid="open-autoenroll-step"
      value={isAutoEnrolling ? 'true' : 'false'}
      onChange={onChangeAutoEnroll}
      label={getAutoEnrollHeader(isS12n, isProfessionalCertificate)}
      supportText={_t('Only learners in this learning program are affected by this configuration')}
      css={{ width: '100%' }}
    >
      <RadioCard
        label={_t('Yes, automatically enroll all existing and future learners')}
        value="true"
        data-testid="enable-course-auto-enroll-radio-button"
      >
        {isAutoEnrolling && (
          <div css={((isAutoEnrolling && hasUpdatedAutoEnroll) || enterpriseAdminUser.isC4CBOrg) && warningStyle}>
            {isAutoEnrolling && hasUpdatedAutoEnroll && (
              <DrawerInfo
                theme="grey"
                text={_t(
                  'Existing learners will be automatically enrolled when this configuration is submitted. Future learners will be automatically enrolled once they join this learning program. This setting can be reverted anytime but once learners are automatically enrolled in a course they cannot be automatically unenrolled.'
                )}
              />
            )}
            {isAutoEnrolling && fixedEndDate && hasUpdatedAutoEnroll && (
              <DrawerInfo
                theme="blue"
                className="m-t-1"
                text={_t(
                  'Auto-enroll will be automatically disabled at the end of the limited availability period selected above.'
                )}
              />
            )}
            {enterpriseAdminUser.isC4CBOrg && hasUpdatedAutoEnroll && (
              <DrawerInfo
                data-testid="c4cb-autoenrollment-warning"
                theme="blue"
                className="m-t-1"
                text={_t(
                  'Coursera for Campus Basic accounts have an enrollment limit of 1 course per year. Automatic enrollments will not work on learners who reached that limit.'
                )}
              />
            )}
            {!enterpriseAdminUser.isC4COrg && hasUpdatedAutoEnroll && (
              <DrawerInfo
                theme="blue"
                className="m-t-1"
                data-testid="non-c4c-autoenrollment-warning"
                text={_t(
                  'If your learning program has applied an enrollment limit per learner, automatic enrollments will not work on learners who have reached that limit.'
                )}
              />
            )}
          </div>
        )}
      </RadioCard>
      <RadioCard
        label={_t('No, do not automatically enroll learners')}
        value="false"
        data-testid="disable-course-auto-enroll-radio-button"
        supportText={!isAutoEnrolling && hasUpdatedAutoEnroll ? getNoOptionSupportText() : null}
      />
    </RadioGroup>
  );
};

export const AutoEnrollStepCollapsed: React.FC<Props & { openStep: () => void }> = ({
  openStep,
  isAutoEnrolling,
  fixedEndDate,
  isS12n,
  isProfessionalCertificate,
  productId,
}) => {
  const { programId, orgId, userId } = useProgramAdminTypeAndPermissionsContext();

  const getHeaderText = () => {
    if (isS12n && isAutoEnrolling) {
      return isProfessionalCertificate
        ? _t('Auto-enroll for this Professional Certificate is enabled')
        : _t('Auto-enroll for this Specialization is enabled');
    } else if (isAutoEnrolling) {
      return _t('Auto-enroll for this course is enabled');
    } else if (isS12n) {
      return isProfessionalCertificate
        ? _t('Auto-enroll for this Professional Certificate is disabled')
        : _t('Auto-enroll for this Specialization is disabled');
    } else {
      return _t('Auto-enroll for this course is disabled');
    }
  };

  const getCourseMessaging = () => {
    if (!isAutoEnrolling) {
      return _t(
        'Enabling auto-enroll will automatic enroll all existing and future learners in this course. Only learners in this learning program will be affected by this configuration.'
      );
    } else if (fixedEndDate) {
      return (
        <FormattedHTMLMessage
          message={_t(
            'All learners in this learning program were automatically enrolled in this course. Future learners will be automatically enrolled when they join the program on or before <strong>{date}</strong>.'
          )}
          date={fixedEndDate}
        />
      );
    } else {
      return _t(
        'All learners in this learning program were automatically enrolled in this Specialization. Future learners will be automatically enrolled when they join the program.'
      );
    }
  };

  const getS12nMessaging = () => {
    if (!isAutoEnrolling) {
      return isProfessionalCertificate
        ? _t(
            'Enabling auto-enroll will automatic enroll all existing and future learners in this Professional Certificate. Only learners in this learning program will be affected by this configuration.'
          )
        : _t(
            'Enabling auto-enroll will automatic enroll all existing and future learners in this Specialization. Only learners in this learning program will be affected by this configuration.'
          );
    } else if (fixedEndDate) {
      return isProfessionalCertificate ? (
        <FormattedHTMLMessage
          message={_t(
            'All learners in this learning program were automatically enrolled in this Professional Certificate. Future learners will be automatically enrolled when they join the program on or before <strong>{date}</strong>.'
          )}
          date={fixedEndDate}
        />
      ) : (
        <FormattedHTMLMessage
          message={_t(
            'All learners in this learning program were automatically enrolled in this Specialization. Future learners will be automatically enrolled when they join the program on or before <strong>{date}</strong>.'
          )}
          date={fixedEndDate}
        />
      );
    } else {
      return isS12n
        ? _t(
            'All learners in this learning program were automatically enrolled in this Specialization. Future learners will be automatically enrolled when they join the program.'
          )
        : _t(
            'All learners in this learning program were automatically enrolled in this Professional Certificate. Future learners will be automatically enrolled when they join the program.'
          );
    }
  };

  return (
    <Grid container spacing={16} direction="column">
      <Grid item>
        <Typography2 variant="subtitleMedium" component="h4">
          {getHeaderText()}
        </Typography2>
        <Typography2 component="p" variant="bodySecondary">
          {isS12n ? getS12nMessaging() : getCourseMessaging()}
        </Typography2>
      </Grid>
      <Grid item>
        <TrackedButton
          trackingName="enterprise_admin_autoenroll_edit_btn"
          trackingData={{
            userId,
            programId,
            orgId,
            productId,
          }}
          variant="secondary"
          onClick={openStep}
          data-testid="autoenroll-step-edit-button"
          size="small"
        >
          {_t('Edit')}
        </TrackedButton>
      </Grid>
    </Grid>
  );
};

export const AutoEnrollStepDisabled: React.FC<{ isS12n?: boolean; isProfessionalCertificate?: boolean }> = ({
  isS12n,
  isProfessionalCertificate,
}) => {
  const getC4CBInfoText = () => {
    if (isS12n) {
      return isProfessionalCertificate
        ? _t(
            'Automatic enrollment for Professional Certificates is not available on accounts that have enrollment limits. Please contact your organization’s administrator for more information.'
          )
        : _t(
            'Automatic enrollment for Specializations is not available on accounts that have enrollment limits. Please contact your organization’s administrator for more information.'
          );
    } else {
      return _t(
        'Automatic enrollment cannot be enabled for this course because the availability period selected has ended. Please select a different availability option or setup a custom availability.'
      );
    }
  };

  const getInfoText = () => {
    if (isS12n) {
      return isProfessionalCertificate
        ? _t(
            'Automatic enrollment cannot be enabled for this Professional Certificate because the availability period selected has ended. Please select a different availability option or setup a custom availability.'
          )
        : _t(
            'Automatic enrollment cannot be enabled for this Specialization because the availability period selected has ended. Please select a different availability option or setup a custom availability.'
          );
    } else {
      return _t(
        'Automatic enrollment cannot be enabled for this course because the availability period selected has ended. Please select a different availability option or setup a custom availability.'
      );
    }
  };

  return (
    <Grid container data-testid="disabled-autoenrollment-step">
      <Typography2 variant="subtitleMedium" component="h4">
        {getAutoEnrollHeader(isS12n, isProfessionalCertificate)}
      </Typography2>
      <Typography2 component="p" variant="bodySecondary">
        {_t('Only learners in this learning program are affected by this configuration')}
      </Typography2>
      {isS12n && enterpriseAdminUser.isC4CBOrg ? (
        <DrawerInfo theme="blue" className="m-t-1" filledIcon text={getC4CBInfoText()} />
      ) : (
        <DrawerInfo theme="blue" className="m-t-1" filledIcon text={getInfoText()} />
      )}
    </Grid>
  );
};

const AutoEnrollStepWrapped: React.FC<Props> = (props) => {
  const { isS12n, isProfessionalCertificate } = useContext(PrivateSessionDrawerContext);
  const [isCollapsed, setIsCollapsed] = useState(!props.isOpen);

  if (props.isAvailabilityEnded || (isS12n && enterpriseAdminUser.isC4CBOrg)) {
    return <AutoEnrollStepDisabled isS12n={isS12n} isProfessionalCertificate={isProfessionalCertificate} />;
  }

  return isCollapsed ? (
    <AutoEnrollStepCollapsed
      {...props}
      openStep={() => setIsCollapsed(false)}
      isS12n={isS12n}
      isProfessionalCertificate={isProfessionalCertificate}
    />
  ) : (
    <AutoEnrollStep {...props} isS12n={isS12n} isProfessionalCertificate={isProfessionalCertificate} />
  );
};

export default AutoEnrollStepWrapped;
